/**
 * AgentsPage - AI Agent Management with MDS Components
 */
import React, { useState, useEffect } from 'react';
import { agentService, AgentDTO } from '../services/agentService';
import {
    McButton,
    McCard,
    McIcon,
    McInput,
    McTag,
} from '@maersk-global/mds-react-wrapper/components-core';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { ReactFlowProvider } from '@xyflow/react';
import AgentBuilder from '../components/agents/AgentBuilder';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';
import '../components/agents/AgentBuilder.css';

interface SavedAgent {
    id: string;
    name: string;
    description?: string;
    nodes: any[];
    edges: any[];
    viewport?: any;
    updatedAt: string;
    status?: string;
    triggerType?: string;
}

const AgentsPage: React.FC = () => {
    const [view, setView] = useState<'list' | 'builder'>('list');
    const [editingAgent, setEditingAgent] = useState<SavedAgent | null>(null);
    const [savedAgents, setSavedAgents] = useState<SavedAgent[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();
    const isAdmin = hasRole(roleConfig.adminAgents);

    useEffect(() => {
        const loadAgents = async () => {
            try {
                const agents = await agentService.list();
                const mapped: SavedAgent[] = agents.map((a: AgentDTO) => {
                    // Prefer structured workflow object; fall back to parsing legacy string
                    const wf = a.workflow
                        ? a.workflow
                        : a.workflowDefinition
                            ? JSON.parse(a.workflowDefinition)
                            : {};
                    const nodes = wf.nodes || [];

                    // Re-inject toolConfigs into tool nodes so the config panel can display them
                    if (a.toolConfigs && Object.keys(a.toolConfigs).length > 0) {
                        for (const node of nodes) {
                            if (node.type === 'tool' && a.toolConfigs[node.id]) {
                                const cfg: Record<string, any> = { ...a.toolConfigs[node.id] };
                                const toolId: string = cfg.toolId || node.data?.toolId || '';
                                if (!node.data) node.data = {};
                                if (!node.data.toolsConfig) node.data.toolsConfig = {};
                                node.data.toolsConfig[toolId] = cfg;
                            }
                        }
                    }

                    return {
                        id: a.agentId || '',
                        name: a.name,
                        description: a.description,
                        nodes,
                        edges: wf.edges || [],
                        viewport: wf.viewport,
                        updatedAt: a.updatedAt || '',
                        status: a.status,
                        triggerType: a.triggerType,
                    };
                });
                setSavedAgents(mapped);
            } catch (err) {
                console.warn('Backend unavailable, loading from localStorage', err);
                const stored = localStorage.getItem('sentinel-agents');
                if (stored) setSavedAgents(JSON.parse(stored));
            }
        };
        loadAgents();
    }, [view]);

    const handleCreateNew = () => {
        if (!isAdmin) return;
        setEditingAgent(null);
        setView('builder');
    };

    const handleOpenAgent = (agent: SavedAgent) => {
        setEditingAgent(agent);
        setView('builder');
    };

    const handleBack = () => {
        setView('list');
        setEditingAgent(null);
    };

    const filteredAgents = savedAgents.filter(a =>
        a.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (view === 'builder') {
        return (
            <ReactFlowProvider>
                <AgentBuilder
                    agentId={editingAgent?.id}
                    initialName={editingAgent?.name || 'Untitled Agent'}
                    initialNodes={editingAgent?.nodes || []}
                    initialEdges={editingAgent?.edges || []}
                    initialStatus={editingAgent?.status || 'DRAFT'}
                    onBack={handleBack}
                    readOnly={!isAdmin}
                />
            </ReactFlowProvider>
        );
    }

    return (
        <>
            {/* Header - follows SchedulesPage pattern */}
            <div style={{ marginBottom: '24px' }}>
                <h1 className="mds-headline--medium-bold" style={{ marginBottom: '8px' }}>
                    AI Agents
                </h1>
                <p className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', margin: 0 }}>
                    Build, configure, and publish intelligent agents for terminal operations. Agents currently support Teams and AI Assistant channels.
                </p>
            </div>

            {/* Actions row */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <McInput
                        label="Search agents"
                        hiddenlabel
                        placeholder="Search agents..."
                        onInput={(e: any) => setSearchTerm(e.target.value)}
                        style={{ width: '300px' }}
                    >
                        <McIcon icon="magnifying-glass" slot="prefix" />
                    </McInput>
                    {isAdmin && (
                        <McTooltip>
                            <McButton
                                slot="trigger"
                                appearance="primary"
                                onClick={handleCreateNew}
                                style={{ width: '180px' }}
                            >
                                <McIcon icon="plus" slot="prefix" />
                                Create New Agent
                            </McButton>
                            <span slot="content">Create a new agent from scratch</span>
                        </McTooltip>
                    )}
                </div>
            </div>

            {/* Saved Agents */}
            <div>
                <h3 className="mds-headline--small-bold" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <McIcon icon="list-bullets" />
                    Your Agents {savedAgents.length > 0 && `(${savedAgents.length})`}
                </h3>

                {filteredAgents.length === 0 ? (
                    <McCard>
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                            <McIcon icon="robot" color="#e0e0e0" style={{ marginBottom: '1rem', width: '48px', height: '48px' }} />
                            <h3 className="mds-headline--x-small-bold">No Agents Found</h3>
                            <p className="mds-text--small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)' }}>
                                Create a new agent using the button above.
                            </p>
                        </div>
                    </McCard>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '1rem' }}>
                        {filteredAgents.map((agent) => (
                            <McCard
                                key={agent.id}
                                onClick={() => handleOpenAgent(agent)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '4px' }}>
                                    <McIcon icon="robot" size="24" />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="mds-text--medium-bold">{agent.name}</div>
                                        <div className="mds-text--small-normal" style={{ color: 'var(--mds-color-neutral-weaker-text)' }}>
                                            {agent.nodes.length} nodes · Updated {new Date(agent.updatedAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <McTag
                                        fit="small"
                                        appearance={agent.status === 'ACTIVE' ? 'success' : agent.status === 'TESTING' ? 'warning' : 'neutral'}
                                    >
                                        {agent.status === 'ACTIVE' ? 'Published' : agent.status === 'TESTING' ? 'Testing' : 'Draft'}
                                    </McTag>
                                </div>
                            </McCard>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};

export default AgentsPage;
